import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCalendarAlt, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import EventCard from './EventCard';
import CreateEventModal from './CreateEventModal';
import CreateClassModal from './CreateClassModal';
import groupService from '../../api/groupService';
import '../../styles/group/UpcomingList.css';

const UpcomingList = ({ groupId, searchQuery, isAdmin, refreshTrigger }) => {
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false); // Track if we had API errors
  
  // Separate states for event and class editing
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentClass, setCurrentClass] = useState(null);

  // Fetch events, classes, and memberships for this group
  const fetchAllItems = async () => {
    try {
      setLoading(true);
      setHasApiError(false); // Reset API error flag
      let allItems = [];
      let eventsError = false;
      let classesError = false;
      
      // Fetch events
      try {
        const eventsResponse = await groupService.getGroupEvents(groupId);
        const events = eventsResponse.data || [];
        
        // Filter to include only future events (upcoming)
        const now = new Date();
        const futureEvents = events.filter(event => new Date(event.date) >= now);
        
        // Add events as-is to ensure consistency with other tabs
        allItems = [...allItems, ...futureEvents];
      } catch (error) {
        console.error('Error fetching events:', error);
        eventsError = true;
      }
      
      // Fetch classes
      try {
        const classesResponse = await groupService.getGroupClasses(groupId);
        // Process classes to make them compatible with EventCard
        const classes = Array.isArray(classesResponse) ? classesResponse : 
                       (classesResponse && Array.isArray(classesResponse.data)) ? classesResponse.data : [];
        
        console.log('🔄 [UpcomingList] Fetched classes with attendee counts:', 
          classes.map(c => `${c.title}: ${c.attendees_count || 0}`).join(', '));
        
        // Transform classes to match event data structure
        const transformedClasses = classes.map(classItem => ({
          ...classItem,
          // Ensure required fields for EventCard exist
          id: classItem.id || Math.random().toString(),
          title: classItem.title || 'Unnamed Class',
          description: classItem.description || 'No description available',
          date: classItem.date || new Date().toISOString(),
          cover_image_url: classItem.image_url || classItem.cover_image_url,
          teacher: classItem.instructor || classItem.teacher || 'No instructor specified',
          // Add a special flag to identify this as a class for the EventCard
          isClassItem: true
        }));
        
        // Filter to include only future classes (using the same now variable)
        const now = new Date();
        const futureClasses = transformedClasses.filter(classItem => new Date(classItem.date) >= now);
        
        // Add classes to the items list
        allItems = [...allItems, ...futureClasses];
        
        console.log('🔄 [UpcomingList] Added classes to upcoming items:', futureClasses.length);
      } catch (error) {
        console.error('Error fetching classes:', error);
        classesError = true;
      }
      
      // If we had API errors and no items, it's likely a token/auth issue
      if ((eventsError || classesError) && allItems.length === 0) {
        setHasApiError(true);
      }
      
      // Sort all items by date (nearest first)
      allItems.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
      
      console.log('🔄 [UpcomingList] Setting upcoming items with fresh data from server');
      setUpcomingItems(allItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching items:', error);
      setError('Please refresh the page to reload items');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllItems();
  }, [groupId, refreshTrigger]); // Add refreshTrigger dependency

  // Listen for visibility change events to refresh when user comes back to tab
  useEffect(() => {
    let focusTimeout;
    let lastFocusTime = 0;
    let fileDialogOpen = false;
    let modalOpen = false;
    
    const handleVisibilityChange = () => {
      // Never refresh if any modal is open or file dialog is active
      const hasAnyModal = document.querySelector('.modal-overlay') || 
                         showEditEventModal || 
                         showEditClassModal;
      
      if (!document.hidden && !fileDialogOpen && !modalOpen && !hasAnyModal) {
        console.log('🔄 [UpcomingList] Tab became visible, refreshing data...');
        fetchAllItems();
      }
    };

    const handleFocus = () => {
      // IMMEDIATELY check for any modals and abort if found
      const hasAnyModal = document.querySelector('.modal-overlay') || 
                         document.querySelector('.modal-container') ||
                         showEditEventModal || 
                         showEditClassModal;
      
      if (hasAnyModal) {
        console.log('🔄 [UpcomingList] Modal detected - completely skipping refresh logic');
        return; // Exit early, don't process anything
      }

      const now = Date.now();
      
      // Check for file input activity (file picker might be open)
      const hasFileInput = document.querySelector('input[type="file"]');
      if (hasFileInput) {
        console.log('🔄 [UpcomingList] File input detected - skipping refresh');
        fileDialogOpen = true;
        setTimeout(() => {
          fileDialogOpen = false;
        }, 15000); // Long timeout for file operations
        return;
      }
      
      // Check if a file dialog is likely open by detecting rapid focus changes
      if (now - lastFocusTime < 2000) {
        console.log('🔄 [UpcomingList] Rapid focus change detected, likely file dialog - skipping refresh');
        fileDialogOpen = true;
        setTimeout(() => {
          fileDialogOpen = false;
        }, 15000);
        return;
      }
      
      // Only refresh if it's been more than 60 seconds since last focus event
      // and no file dialog or modal is suspected to be open
      if (now - lastFocusTime > 60000 && !fileDialogOpen && !modalOpen && !hasAnyModal) {
        lastFocusTime = now;
        
        // Add an even longer delay to avoid interfering with file dialogs and modals
        clearTimeout(focusTimeout);
        focusTimeout = setTimeout(() => {
          // Final check - make absolutely sure no modals are open
          const finalModalCheck = document.querySelector('.modal-overlay') || 
                                 document.querySelector('.modal-container') ||
                                 showEditEventModal || 
                                 showEditClassModal;
          
          if (document.hasFocus() && !fileDialogOpen && !modalOpen && !finalModalCheck && Date.now() - lastFocusTime > 10000) {
            console.log('🔄 [UpcomingList] Window focused after safe delay, refreshing data...');
            fetchAllItems();
          } else {
            console.log('🔄 [UpcomingList] Final check failed - modal still open, skipping refresh');
          }
        }, 10000); // Very long delay
      } else {
        console.log('🔄 [UpcomingList] Focus conditions not met - skipping refresh');
      }
    };

    // Listen for file input clicks to set flag
    const handleFileInputActivity = () => {
      console.log('🔄 [UpcomingList] File input activity detected');
      fileDialogOpen = true;
      clearTimeout(focusTimeout); // Cancel any pending refresh
      setTimeout(() => {
        fileDialogOpen = false;
      }, 20000); // Very long timeout for file operations
    };

    // Listen for custom refresh events from detail pages
    const handleCustomRefresh = (event) => {
      if (event.detail && event.detail.groupId === groupId) {
        // Only refresh if no modals are open
        const hasAnyModal = document.querySelector('.modal-overlay') || 
                           showEditEventModal || 
                           showEditClassModal;
        if (!hasAnyModal) {
          fetchAllItems();
        }
      }
    };

    // Enhanced document click handler
    const handleDocumentClick = (e) => {
      // Check for file input activity
      if (e.target.type === 'file' || e.target.closest('.cover-image-container')) {
        handleFileInputActivity();
      }
      
      // Check for modal interactions - set longer protection
      if (e.target.closest('.modal-overlay') || 
          e.target.closest('.modal-container')) {
        modalOpen = true;
        clearTimeout(focusTimeout); // Cancel any pending refresh
        console.log('🔄 [UpcomingList] Modal interaction detected - setting protection');
        setTimeout(() => {
          modalOpen = false;
        }, 30000); // Very long protection when modal is interacted with
      }
    };

    // Only add minimal event listeners
    document.addEventListener('click', handleDocumentClick, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('upcomingListRefresh', handleCustomRefresh);

    return () => {
      clearTimeout(focusTimeout);
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('upcomingListRefresh', handleCustomRefresh);
    };
  }, [groupId, showEditEventModal, showEditClassModal]); // Modal states trigger re-setup

  // Handle edit event
  const handleEditEvent = (event) => {
    setCurrentEvent(event);
    setShowEditEventModal(true);
  };

  // Handle edit class
  const handleEditClass = (classItem) => {
    setCurrentClass(classItem);
    setShowEditClassModal(true);
  };

  // Handle event update
  const handleEventUpdated = (updatedEvent) => {
    // Fetch fresh data from server to ensure we get all updated fields including ticket pricing
    console.log('🔄 [UpcomingList] Event updated, fetching fresh data from server...');
    fetchAllItems(); // This will get fresh data with updated pricing and all fields
    
    setShowEditEventModal(false);
    setCurrentEvent(null);
  };

  // Handle class update
  const handleClassUpdated = (updatedClass) => {
    // Instead of just updating the local state, fetch fresh data from server
    // to ensure we get the latest image URLs and all updated fields
    console.log('🔄 [UpcomingList] Class updated, fetching fresh data from server...');
    fetchAllItems(); // This will get fresh data with updated image URLs
    
    setShowEditClassModal(false);
    setCurrentClass(null);
  };

  // Handle delete item (event or class)
  const handleDeleteItem = async (itemId, isClass) => {
    try {
      if (isClass) {
        console.log(`Deleting class with ID: ${itemId}`);
        await groupService.deleteClass(groupId, itemId);
        console.log(`Class deleted successfully`);
      } else {
        console.log(`Deleting event with ID: ${itemId}`);
        await groupService.deleteEvent(groupId, itemId);
        console.log(`Event deleted successfully`);
      }
      
      // Remove the item from the UI
      setUpcomingItems(upcomingItems.filter(item => item.id !== itemId));
    } catch (error) {
      console.error(`Error deleting ${isClass ? 'class' : 'event'}:`, error);
      alert(`Failed to delete ${isClass ? 'class' : 'event'}. Please try again.`);
    }
  };

  // Handle page refresh
  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Filter items based on search query
  const filteredItems = searchQuery
    ? upcomingItems.filter(item => {
        const title = item.title || '';
        const description = item.description || '';
        return title.toLowerCase().includes(searchQuery.toLowerCase()) ||
               description.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : upcomingItems;

  if (loading) {
    return (
      <div className="upcoming-loading">
        <div className="loading-spinner"></div>
        <p>Loading events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="upcoming-error">
        <p>{error}</p>
      </div>
    );
  }

  if (filteredItems.length === 0) {
    // Show error state if we detected API errors
    if (hasApiError || error) {
      return (
        <div className="upcoming-error">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faClock} size="4x" />
          </div>
          <h2>Something went wrong...</h2>
          <p>Unable to load upcoming items</p>
          <button 
            className="try-again-button"
            onClick={handleRefreshPage}
          >
            Try Again
          </button>
        </div>
      );
    }
    
    // Show search-specific message if user is searching
    if (searchQuery && searchQuery.trim()) {
      return (
        <div className="upcoming-empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faClock} size="4x" />
          </div>
          <h2>No Results Found</h2>
          <p>Try searching with different keywords</p>
        </div>
      );
    }
    
    // Show normal empty state when not searching
    return (
      <div className="upcoming-empty-state">
        <div className="empty-state-icon">
          <FontAwesomeIcon icon={faClock} size="4x" />
        </div>
        <h2>Nothing Coming Up</h2>
        <p>There are no upcoming events to display</p>
      </div>
    );
  }

  return (
    <div className="upcoming-list">
      {filteredItems.map(item => (
        <EventCard
          key={item.id}
          event={item}
          groupId={groupId}
          isAdmin={isAdmin}
          isClass={item.isClassItem} // Pass isClass flag based on item type
          onEditEvent={item.isClassItem ? handleEditClass : handleEditEvent}
          onEditClass={handleEditClass}
          onDeleteEvent={(itemId) => handleDeleteItem(itemId, item.isClassItem)}
        />
      ))}
      
      {/* Edit Event Modal */}
      {showEditEventModal && currentEvent && (
        <CreateEventModal
          isOpen={showEditEventModal}
          onClose={() => {
            setShowEditEventModal(false);
            setCurrentEvent(null);
          }}
          groupId={groupId}
          onEventUpdated={handleEventUpdated}
          event={currentEvent}
          isEditMode={true}
        />
      )}
      
      {/* Edit Class Modal */}
      {showEditClassModal && currentClass && (
        <CreateClassModal
          isOpen={showEditClassModal}
          onClose={() => {
            setShowEditClassModal(false);
            setCurrentClass(null);
          }}
          groupId={groupId}
          onClassUpdated={handleClassUpdated}
          classItem={currentClass}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default UpcomingList;